/**
 * Admin modal to create a staff account (EIR or CEG). The server mints the
 * temporary password and returns it once; this modal only displays it for the
 * admin to share manually (no email is sent). Calls onCreated() so the parent
 * list can refresh.
 */
import React, { useState } from 'react';
import { createUser } from '../services/apiService';

const EMPTY = { role: 'eir', first_name: '', last_name: '', email: '', phone: '', category: '' };

export default function AddAccountModal({ onClose, onCreated }) {
  const [form, setForm]       = useState(EMPTY);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [created, setCreated] = useState(null); // { name, email, password }

  function set(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      setError('First name, last name, and email are required.');
      return;
    }
    setSaving(true);
    try {
      // No `password` field: the server generates the temp password and returns
      // it as temp_password. This is the only response that carries it.
      const user = await createUser({
        role:       form.role,
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        email:      form.email.trim().toLowerCase(),
        phone:      form.phone.trim() || null,
        category:   form.role === 'eir' ? (form.category.trim() || null) : null,
      });
      setCreated({
        name:     `${form.first_name.trim()} ${form.last_name.trim()}`,
        email:    form.email.trim().toLowerCase(),
        password: user?.temp_password || '',
      });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <h2 style={s.title}>Add Account</h2>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        {created ? (
          <div>
            <p style={s.successMsg}>
              Account created for <strong>{created.name}</strong>.
            </p>
            <div style={s.credBox}>
              <p style={s.credLabel}>Share these credentials with the new user:</p>
              <div style={s.credRow}><span style={s.credKey}>Email</span><span style={s.credVal}>{created.email}</span></div>
              <div style={s.credRow}><span style={s.credKey}>Temp password</span><span style={s.credVal}>{created.password || 'not returned'}</span></div>
            </div>
            {created.password ? (
              <p style={s.credNote}>They will be asked to set their own password when they first log in.</p>
            ) : (
              // The password only exists in that one response — nothing can recover it.
              <p style={s.credNote}>
                The account was created but no temporary password came back. Use
                “Reset password” on the account to issue one.
              </p>
            )}
            <button style={s.doneBtn} onClick={onClose}>Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={s.roleRow}>
              {['eir', 'ceg'].map((r) => (
                <button
                  key={r}
                  type="button"
                  style={{ ...s.roleBtn, ...(form.role === r ? s.roleBtnActive : {}) }}
                  onClick={() => setForm((prev) => ({ ...prev, role: r }))}
                >
                  {r.toUpperCase()}
                </button>
              ))}
            </div>

            <div style={s.row2}>
              <div style={s.field}>
                <label style={s.label}>First Name</label>
                <input style={s.input} value={form.first_name} onChange={set('first_name')} placeholder="Jane" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Last Name</label>
                <input style={s.input} value={form.last_name} onChange={set('last_name')} placeholder="Smith" />
              </div>
            </div>

            <div style={s.field}>
              <label style={s.label}>Email</label>
              <input style={s.input} type="email" value={form.email} onChange={set('email')} placeholder="jane@example.com" />
            </div>

            <div style={s.field}>
              <label style={s.label}>Phone <span style={s.optional}>(optional)</span></label>
              <input style={s.input} type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 555 000 0000" />
            </div>

            {form.role === 'eir' && (
              <div style={s.field}>
                <label style={s.label}>Category / Expertise <span style={s.optional}>(optional)</span></label>
                <input style={s.input} value={form.category} onChange={set('category')} placeholder="SaaS, B2B, GTM" />
                <p style={s.hint}>Comma-separated tags shown on the EIR's profile.</p>
              </div>
            )}

            {error && <p style={s.error}>{error}</p>}

            <div style={s.actions}>
              <button type="button" style={s.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
              <button type="submit" style={s.submitBtn} disabled={saving}>
                {saving ? 'Creating…' : 'Create Account'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const s = {
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:        { background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', padding: 32, width: '100%', maxWidth: 480 },
  header:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title:        { fontFamily: 'var(--font-accent)', fontSize: 22, fontWeight: 800, color: 'var(--color-forest)', margin: 0 },
  closeBtn:     { background: 'none', border: 'none', fontSize: 18, color: 'var(--color-forest)', cursor: 'pointer', opacity: 0.5, lineHeight: 1 },
  roleRow:      { display: 'flex', gap: 8, marginBottom: 20 },
  roleBtn:      { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, padding: '6px 18px', borderRadius: 'var(--radius-full)', border: '1.5px solid var(--color-mint-dark)', background: 'var(--color-frost)', color: 'var(--color-forest)', cursor: 'pointer', letterSpacing: '0.04em' },
  roleBtnActive:{ background: 'var(--color-forest)', color: '#fff', borderColor: 'var(--color-forest)' },
  row2:         { display: 'flex', gap: 12, marginBottom: 0 },
  field:        { marginBottom: 16, flex: 1 },
  label:        { display: 'block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: 'var(--color-forest)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' },
  optional:     { fontWeight: 400, textTransform: 'none', opacity: 0.55 },
  input:        { width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', background: 'var(--color-frost)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', outline: 'none' },
  hint:         { fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--color-forest)', opacity: 0.5, marginTop: 4 },
  error:        { fontFamily: 'var(--font-body)', fontSize: 13, color: '#b71c1c', marginBottom: 12 },
  actions:      { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  cancelBtn:    { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '8px 18px', cursor: 'pointer' },
  submitBtn:    { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--color-forest)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '8px 18px', cursor: 'pointer' },
  successMsg:   { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', marginBottom: 16 },
  credBox:      { background: 'var(--color-frost)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '14px 16px', marginBottom: 12 },
  credLabel:    { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 },
  credRow:      { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 },
  credKey:      { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6 },
  credVal:      { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: 'var(--color-forest)', fontFamily: 'monospace' },
  credNote:     { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--color-forest)', opacity: 0.55, marginBottom: 20 },
  doneBtn:      { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--color-forest)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '8px 20px', cursor: 'pointer' },
};
